import * as React from "react";
import Dialog from "@mui/material/Dialog";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { DialogContent } from "@material-ui/core";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import { CircularProgress, MenuItem, Chip, Switch, FormControlLabel } from "@mui/material";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import AlertDialog from "../../../../components/AlertDialog/index.es6";
import { PRIMARY_THEME_COLOR_SHADE_1 } from "../../../../lib/constants";

var STRATEGIES = [
  {
    value: "prefix_match",
    label: "Prefix match — route by phone number prefix",
  },
  {
    value: "field_value",
    label: "Field value — use an SMS object field as the channel name",
  },
];

function buildState(resolution) {
  if (!resolution) {
    return {
      strategy: "prefix_match",
      field_path: "",
      override: false,
      rules: [],
      value_rules: [],
      default_channel: "",
    };
  }
  return {
    strategy: resolution.strategy || "prefix_match",
    field_path: resolution.field_path || "",
    override: !!resolution.override,
    rules: (resolution.rules || []).map(function(r) {
      return Object.assign({}, r);
    }),
    value_rules: (resolution.value_rules || []).map(function(r) {
      return Object.assign({}, r);
    }),
    default_channel: resolution.default_channel || "",
  };
}

export default function EditChannelResolution(props) {
  var open = props.open;
  var channels = props.channels || [];

  var [state, setState] = React.useState(
    buildState(props.resolution)
  );
  // prefix_match add-row state
  var [newPrefix, setNewPrefix] = React.useState("");
  var [newChannel, setNewChannel] = React.useState("");
  // field_value add-row state
  var [newValue, setNewValue] = React.useState("");
  var [newValueChannel, setNewValueChannel] = React.useState("");

  React.useEffect(
    function() {
      if (open) {
        setState(buildState(props.resolution));
        setNewPrefix("");
        setNewChannel("");
        setNewValue("");
        setNewValueChannel("");
      }
    },
    [open]
  );

  function set(field, value) {
    setState(function(prev) {
      var next = Object.assign({}, prev);
      next[field] = value;
      return next;
    });
  }

  function addRule() {
    if (!newPrefix || !newChannel) return;
    setState(function(prev) {
      return Object.assign({}, prev, {
        rules: prev.rules.concat([{ prefix: newPrefix, channel: newChannel }]),
      });
    });
    setNewPrefix("");
    setNewChannel("");
  }

  function removeRule(idx) {
    setState(function(prev) {
      return Object.assign({}, prev, {
        rules: prev.rules.filter(function(_, i) { return i !== idx; }),
      });
    });
  }

  function addValueRule() {
    if (!newValue || !newValueChannel) return;
    setState(function(prev) {
      return Object.assign({}, prev, {
        value_rules: prev.value_rules.concat([
          { value: newValue, channel: newValueChannel },
        ]),
      });
    });
    setNewValue("");
    setNewValueChannel("");
  }

  function removeValueRule(idx) {
    setState(function(prev) {
      return Object.assign({}, prev, {
        value_rules: prev.value_rules.filter(function(_, i) { return i !== idx; }),
      });
    });
  }

  function handleSubmit() {
    var resolution = {
      strategy: state.strategy,
      override: state.override || undefined,
      default_channel: state.default_channel || undefined,
    };
    if (state.strategy === "prefix_match") {
      resolution.rules = state.rules;
    }
    if (state.strategy === "field_value") {
      resolution.field_path = state.field_path || undefined;
      resolution.value_rules = state.value_rules;
    }
    props.onSave({
      sms_channels: props.smsChannels,
      channel_resolution: resolution,
    });
  }

  function handleSuccess() {
    props.onSaveSuccess();
    props.onClose();
  }

  function handleError() {
    props.onSaveErrorReset();
  }

  var channelOptions = channels.map(function(c) {
    return c.channel_name || "(default)";
  });

  return (
    <>
      <AlertDialog
        custom
        show={props.saveLoading}
        size="sm"
        style={{ marginTop: "0", top: "30vh" }}
        showCancel={false}
        showConfirm={false}
        showTitle={false}
        confirmBtnCssClass
      >
        <CircularProgress />
        <h2>Saving channel resolution...</h2>
      </AlertDialog>

      <AlertDialog
        success
        show={props.saveSuccess}
        size="sm"
        title={"Channel resolution saved"}
        style={{ marginTop: "0", top: "30vh" }}
        onConfirm={handleSuccess}
        confirmBtnText={"OK"}
        showCancel={false}
        confirmBtnCssClass
      />

      <AlertDialog
        danger
        show={!!props.saveError}
        title={"Error saving channel resolution"}
        onConfirm={handleError}
        confirmBtnText={"Try again"}
        showCancel={false}
        style={{ marginTop: "0", top: "30vh" }}
        confirmBtnCssClass
      >
        {props.saveError
          ? typeof props.saveError === "string"
            ? props.saveError
            : props.saveError.message || "Unknown error"
          : ""}
      </AlertDialog>

      <Dialog
        open={open}
        fullWidth
        maxWidth="md"
        onClose={props.onClose}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar style={{ background: PRIMARY_THEME_COLOR_SHADE_1 }}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={props.onClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
              Configure Channel Resolution
            </Typography>
          </Toolbar>
        </AppBar>

        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Define how the SMS executor picks which channel to use when a
              message comes in. Rules are matched top-to-bottom; the first
              match wins. Unmatched messages fall back to the default channel.
            </Typography>

            <ValidatorForm onSubmit={handleSubmit}>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextValidator
                    select
                    fullWidth
                    label="Strategy"
                    value={state.strategy}
                    onChange={function(e) { set("strategy", e.target.value); }}
                    variant="outlined"
                    validators={["required"]}
                    errorMessages={["Strategy is required"]}
                  >
                    {STRATEGIES.map(function(s) {
                      return (
                        <MenuItem key={s.value} value={s.value}>
                          {s.label}
                        </MenuItem>
                      );
                    })}
                  </TextValidator>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextValidator
                    select
                    fullWidth
                    label="Default Channel (fallback)"
                    value={state.default_channel}
                    onChange={function(e) { set("default_channel", e.target.value); }}
                    variant="outlined"
                    helperText="Used when no rule matches (or field value is empty)"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {channelOptions.map(function(name) {
                      return (
                        <MenuItem key={name} value={name}>
                          {name}
                        </MenuItem>
                      );
                    })}
                  </TextValidator>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={state.override}
                        onChange={function(e) { set("override", e.target.checked); }}
                        color="primary"
                      />
                    }
                    label="Override channel if already set in the request"
                  />
                  <Typography variant="caption" color="text.secondary" display="block">
                    When enabled, resolution runs even when the incoming SMS already
                    has a channel specified, replacing it with the resolved value.
                  </Typography>
                </Grid>

                {/* ── field_value strategy ─────────────────────── */}
                {state.strategy === "field_value" && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextValidator
                        fullWidth
                        label="SMS field path"
                        value={state.field_path}
                        onChange={function(e) { set("field_path", e.target.value); }}
                        variant="outlined"
                        validators={["required"]}
                        errorMessages={["Field path is required"]}
                        helperText='Dot-notation path on the SMS object to read. e.g. "channel", "partner_reference", "additional_details.channel"'
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Divider>
                        <Typography variant="caption" color="text.secondary">
                          VALUE RULES
                        </Typography>
                      </Divider>
                    </Grid>

                    {state.value_rules.length > 0 && (
                      <Grid item xs={12}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Field Value</strong></TableCell>
                              <TableCell><strong>Route to Channel</strong></TableCell>
                              <TableCell align="right" />
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {state.value_rules.map(function(rule, idx) {
                              return (
                                <TableRow key={idx}>
                                  <TableCell>
                                    <Chip size="small" label={rule.value} variant="outlined" />
                                  </TableCell>
                                  <TableCell>
                                    <Chip size="small" label={rule.channel} color="primary" />
                                  </TableCell>
                                  <TableCell align="right">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={function() { removeValueRule(idx); }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Box display="flex" gap={2} alignItems="flex-start">
                        <TextValidator
                          label="Field Value"
                          value={newValue}
                          onChange={function(e) { setNewValue(e.target.value); }}
                          variant="outlined"
                          size="small"
                          helperText='e.g. airtel, mtn, probase'
                          style={{ minWidth: 160 }}
                        />
                        <TextValidator
                          select
                          label="Channel"
                          value={newValueChannel}
                          onChange={function(e) { setNewValueChannel(e.target.value); }}
                          variant="outlined"
                          size="small"
                          style={{ minWidth: 160 }}
                        >
                          {channelOptions.map(function(name) {
                            return (
                              <MenuItem key={name} value={name}>
                                {name}
                              </MenuItem>
                            );
                          })}
                        </TextValidator>
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={addValueRule}
                          disabled={!newValue || !newValueChannel}
                          size="small"
                          sx={{ mt: 0.5 }}
                        >
                          Add Rule
                        </Button>
                      </Box>
                    </Grid>
                  </>
                )}

                {/* ── prefix_match rules ───────────────────────── */}
                {state.strategy === "prefix_match" && (
                  <>
                    <Grid item xs={12}>
                      <Divider>
                        <Typography variant="caption" color="text.secondary">
                          RULES
                        </Typography>
                      </Divider>
                    </Grid>

                    {state.rules.length > 0 && (
                      <Grid item xs={12}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <strong>Phone Prefix</strong>
                              </TableCell>
                              <TableCell>
                                <strong>Route to Channel</strong>
                              </TableCell>
                              <TableCell align="right" />
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {state.rules.map(function(rule, idx) {
                              return (
                                <TableRow key={idx}>
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      label={rule.prefix || rule.match || "*"}
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      label={rule.channel}
                                      color="primary"
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={function() { removeRule(idx); }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Box display="flex" gap={2} alignItems="flex-start">
                        <TextValidator
                          label="Phone Prefix"
                          value={newPrefix}
                          onChange={function(e) { setNewPrefix(e.target.value); }}
                          variant="outlined"
                          size="small"
                          helperText='e.g. 097, +26097, * for catch-all'
                          style={{ minWidth: 160 }}
                        />
                        <TextValidator
                          select
                          label="Channel"
                          value={newChannel}
                          onChange={function(e) { setNewChannel(e.target.value); }}
                          variant="outlined"
                          size="small"
                          style={{ minWidth: 160 }}
                        >
                          {channelOptions.map(function(name) {
                            return (
                              <MenuItem key={name} value={name}>
                                {name}
                              </MenuItem>
                            );
                          })}
                        </TextValidator>
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={addRule}
                          disabled={!newPrefix || !newChannel}
                          size="small"
                          sx={{ mt: 0.5 }}
                        >
                          Add Rule
                        </Button>
                      </Box>
                    </Grid>
                  </>
                )}

                <Grid
                  item
                  xs={12}
                  display="flex"
                  justifyContent="flex-end"
                  gap={1}
                  sx={{ mt: 1 }}
                >
                  <Button variant="outlined" onClick={props.onClose}>
                    Cancel
                  </Button>
                  <Button variant="contained" type="submit">
                    Save Resolution
                  </Button>
                </Grid>
              </Grid>
            </ValidatorForm>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
